import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Label, Input } from "@/components/shared";
import { KeyRound, Check, X } from "lucide-react";

export function ChangePasswordCard() {
  const { user, refresh } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const checks = [["8+ characters", pw.length >= 8], ["Uppercase", /[A-Z]/.test(pw)], ["Lowercase", /[a-z]/.test(pw)], ["Number", /\d/.test(pw)], ["Symbol", /[^A-Za-z0-9]/.test(pw)]];
  const strong = checks.every(([, ok]) => ok);
  const mustChange = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) { toast.error("Password doesn't meet the requirements yet"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/profile/change-password", { current_password: cur, new_password: pw });
      toast.success("Password updated — use it next time you sign in");
      setCur(""); setPw(""); setPw2("");
      refresh?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card className={`p-5 space-y-4 ${mustChange ? "!border-[#FF5722] bg-[#FF5722]/10" : ""}`} data-testid="change-password-card">
      <div>
        <div className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5"><KeyRound size={15} className="text-[#FF5722]" /> {mustChange ? "Set your own password now" : "Change Password"}</div>
        <p className="text-zinc-400 text-xs mt-1">{mustChange ? "You're still using the temporary password from your welcome email. Please choose your own password to keep your account secure." : "Pick a new password anytime. You can also sign in with Google using your account email."}</p>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Current password {!user?.has_password && <span className="text-zinc-500 normal-case">(leave blank if you only use Google sign-in)</span>}</Label><Input data-testid="pw-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>New password</Label><Input data-testid="pw-new" type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" required /></div>
          <div><Label>Confirm new password</Label><Input data-testid="pw-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" required /></div>
        </div>
        {pw && (
          <ul className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px]" data-testid="pw-checks">
            {checks.map(([l, ok]) => <li key={l} className={`flex items-center gap-1 ${ok ? "text-emerald-400" : "text-zinc-500"}`}>{ok ? <Check size={11} /> : <X size={11} />} {l}</li>)}
          </ul>
        )}
        <Btn type="submit" data-testid="pw-submit" disabled={busy || !pw}>{busy ? "Saving…" : "Update password"}</Btn>
      </form>
    </Card>
  );
}
